import React from 'react';
import { getFileAssets } from '../../../../../helpers/file-helper';
import { getGroup } from '../../../../../helpers/group-helper';
import { getProjectCustomFields } from '../../../../../helpers/project-helper';
import {
    getTicketById,
    getTicketCategories,
    getTicketFollows,
    getTicketSubcategories,
} from '../../../../../helpers/ticket-helper';
import { getUserList } from '../../../../../helpers/user-helper';
import {
    ADDITIONAL_FIELD_TYPES,
    fileHasTicketReferenceWithoutField,
    fileMatchesReference,
    normalizeTag,
    parseTags,
} from '../helpers';

export const useTaskDetailData = ({ token, ticketId }) => {
    const [ticket, setTicket] = React.useState(null);
    const [attachments, setAttachments] = React.useState([]);
    const [resolutionAttachments, setResolutionAttachments] = React.useState([]);
    const [follows, setFollows] = React.useState([]);
    const [followFilesById, setFollowFilesById] = React.useState({});
    const [categories, setCategories] = React.useState([]);
    const [subcategories, setSubcategories] = React.useState([]);

    const [users, setUsers] = React.useState([]);
    const [groups, setGroups] = React.useState([]);
    const [additionalTabs, setAdditionalTabs] = React.useState([]);
    const [additionalFieldDefs, setAdditionalFieldDefs] = React.useState([]);
    const [additionalFieldValues, setAdditionalFieldValues] = React.useState({});
    const [activeMainTab, setActiveMainTab] = React.useState('content');

    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [isEditingDescription, setIsEditingDescription] = React.useState(false);
    const [isContentTransitioning, setIsContentTransitioning] = React.useState(false);

    const [tagInput, setTagInput] = React.useState('');
    const [openSidebarPanels, setOpenSidebarPanels] = React.useState({
        properties: true,
        requesterInfo: true,
        attachmentsTags: true,
    });

    const [form, setForm] = React.useState({
        title: '',
        description: '',
        type: 'INCIDENT',
        source: 'PORTAL',
        requester: '',
        category: '',
        subcategory: '',
        tags: '',
        status: 'NEW',
        priority: 'MEDIUM',
        impact: 'LOW',
        urgency: 'LOW',
        assignee: '',
        group: '',
    });

    const loadTicket = React.useCallback(async () => {
        if (!token || !ticketId) {
            setError('No se pudo validar la sesión o el ticket.');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        const [ticketResp, filesResp, followsResp] = await Promise.all([
            getTicketById(token, ticketId),
            getFileAssets(token, {
                referenceType: 'TICKET',
                referenceId: ticketId,
                includeDeleted: false,
                limit: 100,
                offset: 0,
            }),
            getTicketFollows(token, ticketId, {
                includePrivate: true,
                limit: 100,
                offset: 0,
            }),
        ]);

        if (!ticketResp?.success || !ticketResp?.data) {
            setError(ticketResp?.message || 'No se pudo cargar el ticket.');
            setTicket(null);
            setIsLoading(false);
            return;
        }

        const ticketData = ticketResp.data;
        const allFiles = Array.isArray(filesResp?.data) ? filesResp.data : [];
        const nextFollows = Array.isArray(followsResp?.data) ? followsResp.data : [];

        setTicket(ticketData);
        setIsEditingDescription(false);
        setForm({
            title: ticketData.title || '',
            description: ticketData.description || '',
            type: ticketData.type || 'INCIDENT',
            source: ticketData.source || 'PORTAL',
            requester: ticketData.requester?._id || '',
            category: ticketData.category?._id || '',
            subcategory: ticketData.subcategory?._id || '',
            tags: Array.isArray(ticketData.tags) ? ticketData.tags.join(', ') : '',
            status: ticketData.status || 'NEW',
            priority: ticketData.priority || 'MEDIUM',
            impact: ticketData.impact || 'LOW',
            urgency: ticketData.urgency || 'LOW',
            assignee: ticketData.assignee?._id || '',
            group: ticketData.group || '',
        });

        setAttachments(allFiles.filter((file) => {
            return fileMatchesReference(file, { refType: 'TICKET', refId: ticketId, refField: 'attachments' })
                || fileHasTicketReferenceWithoutField(file, ticketId);
        }));

        setResolutionAttachments(allFiles.filter((file) => {
            return fileMatchesReference(file, { refType: 'TICKET', refId: ticketId, refField: 'resolution' });
        }));

        setFollows(nextFollows);

        const followFilesResponses = await Promise.all(nextFollows.map((follow) => {
            return getFileAssets(token, {
                referenceType: 'TICKET_FOLLOW',
                referenceId: follow._id,
                includeDeleted: false,
                limit: 20,
                offset: 0,
            });
        }));

        const nextFollowFilesById = {};
        nextFollows.forEach((follow, index) => {
            nextFollowFilesById[follow._id] = Array.isArray(followFilesResponses[index]?.data)
                ? followFilesResponses[index].data
                : [];
        });
        setFollowFilesById(nextFollowFilesById);

        setIsLoading(false);
    }, [token, ticketId]);

    const loadSidebarCatalogs = React.useCallback(async (projectId = '') => {
        if (!token) {
            setUsers([]);
            setGroups([]);
            setAdditionalTabs([]);
            setAdditionalFieldDefs([]);
            setAdditionalFieldValues({});
            return;
        }

        const [usersResp, groupsResp] = await Promise.all([
            getUserList(token, { limit: 200, offset: 0, status: 'ACTIVE' }),
            getGroup(token, { limit: 200, offset: 0, status: 'ACTIVE' }),
        ]);

        setUsers(Array.isArray(usersResp?.data) ? usersResp.data : []);
        setGroups(Array.isArray(groupsResp?.data) ? groupsResp.data : []);

        if (!projectId) {
            setAdditionalTabs([]);
            setAdditionalFieldDefs([]);
            setAdditionalFieldValues({});
            return;
        }

        const customFieldsResp = await getProjectCustomFields(token, projectId);
        const rawCustomFieldDefs = Array.isArray(customFieldsResp?.data?.customFields)
            ? customFieldsResp.data.customFields
            : (Array.isArray(customFieldsResp?.data) ? customFieldsResp.data : []);
        const customFieldDefs = rawCustomFieldDefs.filter((field) => String(field?.scope || 'TICKET').toUpperCase() === 'TICKET');
        const customFieldGroups = Array.isArray(customFieldsResp?.data?.customFieldGroups)
            ? customFieldsResp.data.customFieldGroups
            : [];

        const groupNameById = customFieldGroups.reduce((accumulator, groupItem) => {
            if (groupItem?._id) {
                accumulator[groupItem._id] = groupItem.name || 'General';
            }
            return accumulator;
        }, {});

        const customFieldValues = ticket?.customFields || {};

        setAdditionalFieldDefs(customFieldDefs);
        setAdditionalFieldValues(() => {
            const nextValues = {};
            customFieldDefs.forEach((field) => {
                const key = field?.key || field?._id;
                if (!key) return;

                if (Object.prototype.hasOwnProperty.call(customFieldValues, key)) {
                    const sourceValue = customFieldValues[key];
                    if (field.type === ADDITIONAL_FIELD_TYPES.CHECKBOX) {
                        nextValues[key] = Boolean(sourceValue);
                        return;
                    }

                    if (sourceValue === null || sourceValue === undefined) {
                        nextValues[key] = '';
                        return;
                    }

                    nextValues[key] = sourceValue;
                    return;
                }

                nextValues[key] = field.type === ADDITIONAL_FIELD_TYPES.CHECKBOX ? false : '';
            });
            return nextValues;
        });

        const grouped = {};

        customFieldDefs.forEach((definition) => {
            const key = definition?.key || definition?._id;
            if (!key) {
                return;
            }

            const fieldGroupValue = definition?.fieldGroup;
            const groupId = typeof fieldGroupValue === 'object' ? fieldGroupValue?._id : fieldGroupValue;
            const groupNameFromDef = typeof fieldGroupValue === 'object' ? fieldGroupValue?.name : '';
            const groupName = groupNameFromDef || groupNameById[groupId] || 'Otros';

            if (!grouped[groupName]) {
                grouped[groupName] = [];
            }

            grouped[groupName].push({
                key: String(key),
                label: definition?.name || key,
                type: definition?.type || ADDITIONAL_FIELD_TYPES.SHORT_TEXT,
                required: Boolean(definition?.required),
                options: Array.isArray(definition?.options) ? definition.options : [],
            });
        });

        const tabs = Object.entries(grouped).map(([groupName, fields], index) => ({
            key: `group-${index}`,
            label: groupName,
            fields,
        }));

        setAdditionalTabs(tabs);
    }, [token, ticket?.customFields]);

    const mainTabs = React.useMemo(() => {
        const baseTabs = [
            { key: 'content', label: 'General' },
            { key: 'classification', label: 'Clasificación' },
        ];

        const additionalGroupTabs = additionalTabs.map((tab) => ({
            key: `additional-${tab.key}`,
            label: tab.label,
        }));

        return [...baseTabs, ...additionalGroupTabs];
    }, [additionalTabs]);

    React.useEffect(() => {
        const validKeys = new Set(mainTabs.map((tab) => tab.key));
        if (!validKeys.has(activeMainTab)) {
            setActiveMainTab('content');
        }
    }, [mainTabs, activeMainTab]);

    React.useEffect(() => {
        loadTicket();
    }, [loadTicket]);

    React.useEffect(() => {
        loadSidebarCatalogs(ticket?.project?._id || '');
    }, [loadSidebarCatalogs, ticket?.project?._id]);

    React.useEffect(() => {
        if (isLoading || !ticket?._id) {
            return undefined;
        }

        setIsContentTransitioning(true);
        const timerId = setTimeout(() => {
            setIsContentTransitioning(false);
        }, 240);

        return () => {
            clearTimeout(timerId);
        };
    }, [isLoading, ticket?._id]);

    React.useEffect(() => {
        const loadTicketCatalogs = async () => {
            const projectId = ticket?.project?._id;
            if (!token || !projectId) {
                setCategories([]);
                setSubcategories([]);
                return;
            }

            const categoryResp = await getTicketCategories(token, projectId);
            const nextCategories = Array.isArray(categoryResp?.data) ? categoryResp.data : [];
            setCategories(nextCategories);

            const subcategoryResp = await getTicketSubcategories(token, projectId, form.category ? { category: form.category } : {});
            const nextSubcategories = Array.isArray(subcategoryResp?.data) ? subcategoryResp.data : [];
            setSubcategories(nextSubcategories);
        };

        loadTicketCatalogs();
    }, [token, ticket?.project?._id, form.category]);

    const handleInputChange = ({ target }) => {
        const { name, value } = target;
        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleFormSelectChange = React.useCallback((fieldName, nextValue) => {
        setForm((previous) => ({
            ...previous,
            [fieldName]: nextValue,
        }));
    }, []);

    const tagBadges = React.useMemo(() => parseTags(form.tags), [form.tags]);

    const syncTagBadges = React.useCallback((nextTags) => {
        const uniqueTags = Array.from(new Set((Array.isArray(nextTags) ? nextTags : []).map((tag) => normalizeTag(tag)).filter(Boolean)));
        setForm((previous) => ({
            ...previous,
            tags: uniqueTags.join(', '),
        }));
    }, []);

    const handleAddTag = React.useCallback(() => {
        const nextTag = normalizeTag(tagInput);
        if (!nextTag) {
            return;
        }

        syncTagBadges([...tagBadges, nextTag]);
        setTagInput('');
    }, [tagBadges, tagInput, syncTagBadges]);

    const handleRemoveTag = React.useCallback((tagToRemove) => {
        syncTagBadges(tagBadges.filter((tag) => tag !== tagToRemove));
    }, [tagBadges, syncTagBadges]);

    const handleAdditionalFieldChange = ({ target }, fieldKey) => {
        const { type, checked, value } = target;
        setAdditionalFieldValues((previous) => ({
            ...previous,
            [fieldKey]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleAdditionalFieldSelectChange = React.useCallback((fieldKey, nextValue) => {
        setAdditionalFieldValues((previous) => ({
            ...previous,
            [fieldKey]: nextValue,
        }));
    }, []);

    const handleToggleSidebarPanel = React.useCallback((panelKey) => {
        setOpenSidebarPanels((previous) => ({
            ...previous,
            [panelKey]: !previous[panelKey],
        }));
    }, []);

    return {
        ticket,
        attachments,
        resolutionAttachments,
        follows,
        followFilesById,
        categories,
        subcategories,
        users,
        groups,
        additionalTabs,
        additionalFieldDefs,
        additionalFieldValues,
        activeMainTab,
        isLoading,
        error,
        isEditingDescription,
        isContentTransitioning,
        tagInput,
        openSidebarPanels,
        form,
        mainTabs,
        handleInputChange,
        handleFormSelectChange,
        tagBadges,
        handleAddTag,
        handleRemoveTag,
        handleAdditionalFieldChange,
        handleAdditionalFieldSelectChange,
        handleToggleSidebarPanel,
        setForm,
        setTagInput,
        setIsEditingDescription,
        setActiveMainTab,
        setFollows,
        loadTicket,
    };
};